import { PUBLIC_EMAIL_DOMAINS } from './assets';

export function validateEmail(email: string): boolean {
  var re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}

export function isValidHttpUrl(string: string) {
  try {
    const newUrl = new URL(string.replace('{', '').replace('}', ''));
    return newUrl.protocol === 'http:' || newUrl.protocol === 'https:';
  } catch (err) {
    return false;
  }
}

export function isValidSsn(ssn: string): boolean {
  if (!ssn) return false;
  ssn = ssn.trim().replace('-', '').replace(' ', '');
  if (ssn.length !== 10) return false;
  let sSum =
    3 * parseInt(ssn.substr(0, 1), 10) +
    2 * parseInt(ssn.substr(1, 1), 10) +
    7 * parseInt(ssn.substr(2, 1), 10) +
    6 * parseInt(ssn.substr(3, 1), 10) +
    5 * parseInt(ssn.substr(4, 1), 10) +
    4 * parseInt(ssn.substr(5, 1), 10) +
    3 * parseInt(ssn.substr(6, 1), 10) +
    2 * parseInt(ssn.substr(7, 1), 10);
  let modRes = sSum % 11;
  if (modRes > 0) modRes = 11 - modRes;
  if (modRes !== parseInt(ssn.substr(8, 1), 10)) return false;

  const century = parseInt(ssn.substr(9, 1), 10);
  if (isNaN(century)) return false;

  return [0, 8, 9].includes(century);
}

export const validatePasswordLength = (password: string = '') => password.length >= 6;

export const isOnlyDigits = (value: string) => /^\d+$/.test(value);
export const isDecimal = (value: string) => !isNaN(Number(value));
export const bankingNumber = (value: string) => value.length <= 4;
export const ledger = (value: string) => value.length === 2;
export const accountNumber = (value: string) => value.length <= 6;
export const matchesRegex = (value: string, regex: RegExp) => new RegExp(regex).test(value);
export const isColorCode = (value: string) => /^#([0-9a-f]{3}){1,2}$/i.test(value);
export const isOnlyPosDigits = (value: string) => /^\d+$/.test(value) && Number(value) > 0;
export const isValidJson = (json: string): boolean => {
  try {
    JSON.parse(json);
  } catch (e) {
    return false;
  }
  return true;
};
export const isValidUrl = (url: string): boolean => {
  const pattern = new RegExp(
    '^https?:\\/\\/' +
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' +
      '((\\d{1,3}\\.){3}\\d{1,3}))' +
      '(\\:\\d+)?' +
      '(\\/[-a-z\\d%_.~+]*)*' +
      '(\\?[;&a-z\\d%_.~+=-]*)?' +
      '(\\#[-a-z\\d_]*)?$',
    'i'
  );
  return !!pattern.test(url);
};

/*
 Info on Alphanumeric Sender ID
 https://www.twilio.com/docs/glossary/what-alphanumeric-sender-id
 */
export const SMS_SENDER_NAME_MAX_LENGTH = 11;

export const isValidSmsSenderName = (value: string) => {
  if (value.length > SMS_SENDER_NAME_MAX_LENGTH) {
    return false;
  }
  const validCharactersRegex = /^[a-zA-Z0-9 ]+$/;
  const onlyNumeralsRegex = /^\d+$/;

  return validCharactersRegex.test(value) && !onlyNumeralsRegex.test(value);
};

export function isValidDateString(value: string) {
  return !isNaN(Date.parse(value));
}

export function isValidEmailSenderName(name: string): boolean {
  if (!name.trim()) {
    return false;
  }
  const invalidChars = /[<>;,"]/;
  return !invalidChars.test(name);
}

export const parseHttpsUrl = (input: string) => {
  const toHttpsUrl = (url: string): string | null => {
    try {
      const parsedUrl = new URL(url);

      if (!isValidUrl(parsedUrl.href)) return null;

      if (parsedUrl.protocol === 'http:') {
        return url.replace('http:', 'https:');
      }

      return url;
    } catch (e) {
      return null;
    }
  };

  let maybeUrl = toHttpsUrl(input);
  if (maybeUrl) return maybeUrl;

  maybeUrl = toHttpsUrl(`https://${input}`);
  if (maybeUrl) return maybeUrl;

  return null;
};

export function isBusinessEmail(email: string) {
  const atIndex = email.lastIndexOf('@');
  if (atIndex === -1) {
    return false;
  }

  const domainTokens = email
    .slice(atIndex + 1)
    .toLowerCase()
    .split('.');

  return !domainTokens.some((token) => PUBLIC_EMAIL_DOMAINS.includes(token));
}
